import React, { useState, useEffect, useCallback } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Container,
    Table,
    Label,
    FormGroup,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalFooter,
    ModalBody,
    Alert,
    Form,
    Input,
    Spinner,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Collapse
} from 'reactstrap';
import { connect } from 'react-redux';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageDataLoader from '../Utility/PageDataLoader';
import Paginator from '../Utility/Paginator';
import { getBatchExecution } from '../../api/batchExecution';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addNewBatchExecution, bulkBatchExecutionPublish, bulkDeleteBatchExecution } from '../../api/execution';
import { bulkPublishBatchExecution } from '../../api/batchExecution';
import { getBatchList, getBatchChildrenList, getBatchInfo } from '../../api/batch';
import FormUpload from '../../components/FormUpload';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';
import {
    VIEW_EXECUTIONS,
    ADD_EXECUTION,
    DELETE_EXECUTION,
    PUBLISH_EXECUTION
} from '../Utility/PermissionKeys';
import { validatePermissionKey } from '../../helpers/permission';
import { FaPlus, FaMinus, FaYoutube } from 'react-icons/fa';
import { Link } from 'react-router-dom/cjs/react-router-dom';

const isValidYouTubeLink = (link) => {
    const pattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return pattern.test(link);
};

const BulkDeleteExecutionModal = ({
    modalOpen,
    closeModal,
    batch,
    onDeleteConfirmed,
    onSuccess,
    onError
}) => {
    const [deleting, setDeleting] = useState(false);
    const handleDelete = async () => {
        try {
            setDeleting(true);
            const response = await bulkDeleteBatchExecution(batch.batchId, batch.date);
            if (response.error) {
                onError(response.error || 'Failed to delete executions');
            } else {
                onSuccess('Executions deleted successfully');
                onDeleteConfirmed();
            }
        } catch (error) {
            onError('An error occurred while deleting');
        } finally {
            setDeleting(false);
            closeModal();
        }
    };
    return (
        <Modal isOpen={modalOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>Confirm Delete</ModalHeader>
            <ModalBody>
                <h6>
                    Are you sure you want to delete all executions for the batch{' '}
                    <strong>{batch?.name}</strong>?
                </h6>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={closeModal} disabled={deleting}>
                    Cancel
                </Button>
                <Button color="danger" onClick={handleDelete} disabled={deleting}>
                    {deleting ? (
                        <>
                            <Spinner size="sm" className="mr-2" />
                            Deleting...
                        </>
                    ) : (
                        'Delete'
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const BatchExecutionListPage = ({ authUser }) => {
    const history = useHistory();
    const [breadcrumbItems] = useState([
        { title: 'Batch Executions', link: '#' },
        { title: 'View List', link: '#' }
    ]);
    const [batchExecutionList, setBatchExecutionList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageLimit, setPageLimit] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [filterError, setFilterError] = useState('');
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [currentFilterCount, setCurrentFilterCount] = useState(0);
    const [publishingBatchId, setPublishingBatchId] = useState(null);
    const [deletingBatchId, setDeletingBatchId] = useState(null);
    const [tempStartDate, setTempStartDate] = useState(null);
    const [tempEndDate, setTempEndDate] = useState(null);
    const [tempIncludeDeleted, setTempIncludeDeleted] = useState(false);
    const [tempBatchIds, setTempBatchIds] = useState([]);
    const [tempStatus, setTempStatus] = useState('');
    const [appliedStartDate, setAppliedStartDate] = useState(null);
    const [appliedEndDate, setAppliedEndDate] = useState(null);
    const [appliedIncludeDeleted, setAppliedIncludeDeleted] = useState(false);
    const [appliedBatchIds, setAppliedBatchIds] = useState([]);
    const [appliedStatus, setAppliedStatus] = useState('');
    const [sortingRule, setSortingRule] = useState('date$desc');
    const [successAlert, setSuccessAlert] = useState(false);
    const [errorAlert, setErrorAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [executionArray, setExecutionArray] = useState([
        {
            child: null,
            note: '',
            isPresent: true,
            images: [],
            links: [],
            multimediaOpen: false
        }
    ]);
    const [batchOptions, setBatchOptions] = useState([]);
    const [allChildOptions, setAllChildOptions] = useState([]); // Store all children
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [loadingChildren, setLoadingChildren] = useState(false);
    const [formError, setFormError] = useState('');
    const [commonBatch, setCommonBatch] = useState(null);
    const [noteRequired, setNoteRequired] = useState(true);
    const [commonDate, setCommonDate] = useState(new Date());
    const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
    const [batchToDelete, setBatchToDelete] = useState(null);
    const [filterBatchOptions, setFilterBatchOptions] = useState([]);
    const [loadingFilterBatches, setLoadingFilterBatches] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [bulkPublishing, setBulkPublishing] = useState(false);
    const [savingAsDraft, setSavingAsDraft] = useState(false);
    const [publishingNow, setPublishingNow] = useState(false);

    const showSuccessAlert = useCallback((message) => {
        setAlertMessage(message);
        setSuccessAlert(true);
    }, []);

    const showErrorAlert = useCallback((message) => {
        setAlertMessage(message);
        setErrorAlert(true);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        const queryOptions = {
            page: currentPage,
            limit: pageLimit,
            sorting_option: sortingRule,
            filter_date: appliedStartDate ? moment(appliedStartDate).format('YYYY-MM-DD') : '',
            batch_ids: appliedBatchIds.length > 0 ? appliedBatchIds : undefined,
            status: appliedStatus
        };
        try {
            const response = await getBatchExecution(queryOptions);
            if (response) {
                setBatchExecutionList(response.data);
                setTotalEntries(response.pagination.total);
                setTotalPages(response.pagination.pages);
                setLoading(false);
            }
        } catch (error) {
            showErrorAlert('Failed to load batch executions');
            setLoading(false);
        }
    }, [
        currentPage,
        pageLimit,
        sortingRule,
        appliedStartDate,
        appliedEndDate,
        appliedIncludeDeleted,
        appliedBatchIds,
        appliedStatus,
    ]);

    useEffect(() => {
        let count = 0;
        if (appliedStartDate) count++;
        if (appliedEndDate) count++;
        if (appliedIncludeDeleted) count++;
        if (appliedBatchIds.length > 0) count++;
        if (appliedStatus) count++;
        setCurrentFilterCount(count);
    }, [appliedStartDate, appliedEndDate, appliedIncludeDeleted, appliedBatchIds, appliedStatus]);

    useEffect(() => {
        setSelectedRows([]);
    }, [batchExecutionList]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePagination = (page) => {
        setCurrentPage(page);
    };

    const fetchBatchesForFilter = useCallback(async () => {
        setLoadingFilterBatches(true);
        try {
            const batchesData = await getBatchList(1, 1000, {}, 'name$asc');
            if (batchesData) {
                setFilterBatchOptions(batchesData.data.map(batch => ({
                    value: batch._id,
                    label: batch.name
                })));
            }
        } catch (e) {
            showErrorAlert('Failed to load batches for filter');
        } finally {
            setLoadingFilterBatches(false);
        }
    }, [showErrorAlert]);

    const openFilterModal = () => {
        setTempStartDate(appliedStartDate);
        setTempEndDate(appliedEndDate);
        setTempIncludeDeleted(appliedIncludeDeleted);
        setTempBatchIds(appliedBatchIds);
        setTempStatus(appliedStatus);
        if (filterBatchOptions.length === 0) {
            fetchBatchesForFilter();
        }
        setFilterModalOpen(true);
    };

    const applyFilters = () => {
        setAppliedStartDate(tempStartDate);
        setAppliedEndDate(tempEndDate);
        setAppliedIncludeDeleted(tempIncludeDeleted);
        setAppliedBatchIds(tempBatchIds);
        setAppliedStatus(tempStatus);
        setFilterError('');
        setFilterModalOpen(false);
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setTempStartDate(null);
        setTempEndDate(null);
        setTempIncludeDeleted(false);
        setTempBatchIds([]);
        setTempStatus('');
        setFilterError('');
        setAppliedStartDate(null);
        setAppliedEndDate(null);
        setAppliedIncludeDeleted(false);
        setAppliedBatchIds([]);
        setAppliedStatus('');
        setCurrentPage(1);
        setFilterModalOpen(false);
    };

    const getStatusBadge = (pending, total, draft) => {
        if (pending > 0) {
            return <Badge style={{ fontWeight: 'bold', fontSize: '12px' }} color="secondary">Draft</Badge>;
        } else if (draft > 0 && pending === 0) {
            return <Badge style={{ fontWeight: 'bold', fontSize: '12px' }} color="warning">Ready to Publish</Badge>;
        } else if (draft === 0 && pending === 0) {
            return <Badge style={{ fontWeight: 'bold', fontSize: '12px' }} color="success">Published</Badge>;
        }
        return <Badge color="light">No Executions</Badge>;
    };

    const openAddModal = useCallback(async () => {
        setAddModalOpen(true);
        setFormError('');
        setExecutionArray([
            {
                child: null,
                note: '',
                isPresent: true,
                images: [],
                links: [],
                multimediaOpen: false
            }
        ]);
        setCommonBatch(null);
        setCommonDate(new Date());
        setAllChildOptions([]);
        try {
            setLoadingBatches(true);
            const batchesData = await getBatchList(1, 1000, {}, 'name$asc');
            if (batchesData) {
                setBatchOptions(batchesData.data.map(batch => ({
                    value: batch._id,
                    label: batch.name
                })));
            }
        } catch (e) {
            showErrorAlert('Failed to load batches');
        } finally {
            setLoadingBatches(false);
        }
    }, [showErrorAlert]);

    // Get available children for a specific row
    const getAvailableChildren = (currentIndex) => {
        // Get IDs of children already selected in other rows
        const selectedIds = executionArray
            .filter((_, idx) => idx !== currentIndex)
            .map(exec => exec.child?.value)
            .filter(id => id);
        
        // Filter out children that are selected in other rows
        return allChildOptions.filter(
            child => !selectedIds.includes(child.value)
        );
    };

    useEffect(() => {
        if (commonBatch) {
            const fetchChildren = async () => {
                setLoadingChildren(true);
                try {
                    const [childrenData, batchInfo] = await Promise.all([
                        getBatchChildrenList(commonBatch.value, 1, 1000),
                        getBatchInfo(commonBatch.value)
                    ]);
                    if (batchInfo) {
                        setNoteRequired(batchInfo.noteRequired !== false);
                    } else {
                        setNoteRequired(true);
                    }
                    if (childrenData) {
                        // Store all children
                        const options = childrenData.data.map(child => ({
                            value: child._id,
                            label: child.name
                        }));
                        setAllChildOptions(options);
                    }
                } catch (e) {
                    showErrorAlert('Failed to load children for selected batch');
                } finally {
                    setLoadingChildren(false);
                }
            };
            fetchChildren();
        } else {
            setAllChildOptions([]);
            setNoteRequired(true);
        }
    }, [commonBatch, showErrorAlert]);

    const handleChildSelection = (index, selectedItem) => {
        let newExecutionArray = [...executionArray];
        newExecutionArray[index]['child'] = selectedItem;
        setExecutionArray(newExecutionArray);
    };

    const handleNote = (index, e) => {
        let value = e.target.value;
        let newExecutionArray = [...executionArray];
        newExecutionArray[index]['note'] = value;
        setExecutionArray(newExecutionArray);
    };

    const handleIsPresent = (index, value) => {
        let newExecutionArray = [...executionArray];
        newExecutionArray[index]['isPresent'] = value;
        setExecutionArray(newExecutionArray);
    };

    const addExecutionFields = () => {
        let newExecutionArray = [...executionArray];
        newExecutionArray.push({
            child: null,
            note: '',
            isPresent: true,
            images: [],
            links: [],
            multimediaOpen: false
        });
        setExecutionArray(newExecutionArray);
    };

    const removeExecutionFields = (i) => {
        let newExecutionArray = [...executionArray];
        newExecutionArray.splice(i, 1);
        setExecutionArray(newExecutionArray);
    };

    const toggleMultimedia = (index) => {
        const newExecutionArray = [...executionArray];
        newExecutionArray[index].multimediaOpen = !newExecutionArray[index].multimediaOpen;
        setExecutionArray(newExecutionArray);
    };

    const handleAddLink = (index, link) => {
        setFormError('');
        if (!isValidYouTubeLink(link)) {
            setFormError("Enter valid YouTube link!");
            return;
        }
        const trimmedLink = link.trim();
        if (trimmedLink !== '') {
            setExecutionArray(prevExecutionArray => {
                const newExecutionArray = [...prevExecutionArray];
                newExecutionArray[index]['links'] = [...(newExecutionArray[index]['links'] || []), link];
                return newExecutionArray;
            });
        }
    };

    const handleRemoveLink = (index, linkToRemove) => {
        setExecutionArray(prevExecutionArray => {
            const newExecutionArray = [...prevExecutionArray];
            newExecutionArray[index]['links'] = newExecutionArray[index]['links'].filter(link => link !== linkToRemove);
            return newExecutionArray;
        });
    };

    const handleAddExecution = async (action) => {
        setFormError('');
        const isPublishing = action === 'publish';
        if (!commonBatch) {
            setFormError("Please select a batch");
            return;
        }
        if (!commonDate) {
            setFormError("Please select a date");
            return;
        }
        let success = true;
        let nonMarked = executionArray.filter((k) => k.isPresent === null || k.isPresent === undefined);
        if (nonMarked.length > 0) {
            setFormError("Please mark attendance for all children");
            return;
        }
        let nonSelected = executionArray.filter((k) => !k.child);
        if (nonSelected.length > 0) {
            setFormError("Please select child for all executions");
            return;
        }
        if (noteRequired !== false) {
            const missingNote = executionArray.find((e) => e.note.trim() === '');
            if (missingNote) {
                setFormError("Please enter a note for all children");
                return;
            }
        }

        try {
            if (isPublishing) {
                setPublishingNow(true);
            } else {
                setSavingAsDraft(true);
            }

            for (const e of executionArray) {
                let form = new FormData();
                form.set('batch', commonBatch.value);
                form.set('date', moment(commonDate).format('YYYY-MM-DD'));
                form.set('child', e.child.value);
                form.set('note', e.note);
                form.set('isPresent', e.isPresent);
                form.set('draft', !isPublishing);
                form.set('links', JSON.stringify(e.links || []));
                if (e.images && e.images.length > 0) {
                    e.images.forEach((g) => form.append('images', g));
                }
                let response = await addNewBatchExecution(form);
                if (response.error) {
                    setFormError(response.error.message || 'Failed to create execution');
                    success = false;
                    break;
                }
            }

            if (success) {
                const message = isPublishing
                    ? 'Executions published successfully!'
                    : 'Executions created successfully!';
                showSuccessAlert(message);
                setAddModalOpen(false);
                fetchData();
            }
        } catch (error) {
            setFormError('An unexpected error occurred');
            console.error('Error creating execution:', error);
        } finally {
            if (isPublishing) {
                setPublishingNow(false);
            } else {
                setSavingAsDraft(false);
            }
        }
    };

    const handlePublishBatch = useCallback(async (batchId, date) => {
        try {
            setPublishingBatchId(batchId);
            const response = await bulkBatchExecutionPublish(batchId, date);
            if (response.error) {
                showErrorAlert(response.error.message || 'Failed to publish batch');
            } else {
                showSuccessAlert('Batch published successfully!');
                fetchData();
            }
        } catch (error) {
            showErrorAlert('An error occurred while publishing');
            console.error('Publishing error:', error);
        } finally {
            setPublishingBatchId(null);
        }
    }, [fetchData, showErrorAlert, showSuccessAlert]);

    const handleDeleteBatch = (batch) => {
        setBatchToDelete(batch);
        setBulkDeleteModalOpen(true);
    };

    const handleDeleteConfirmed = () => {
        fetchData();
        setBatchToDelete(null);
    };

    const toggleRowSelection = (batch) => {
        setSelectedRows(prev => {
            const existingIndex = prev.findIndex(item =>
                item.batchId === batch.batchId && item.date === batch.date
            );
            if (existingIndex >= 0) {
                return prev.filter((_, index) => index !== existingIndex);
            } else {
                return [...prev, {
                    batchId: batch.batchId,
                    date: batch.date,
                    batchName: batch.batchName
                }];
            }
        });
    };

    const selectAllRows = () => {
        if (selectedRows.length === batchExecutionList.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(batchExecutionList.map(batch => ({
                batchId: batch.batchId,
                date: batch.date,
                batchName: batch.batchName
            })));
        }
    };

    const handleBulkPublish = useCallback(async () => {
        if (selectedRows.length === 0) return;
        try {
            setBulkPublishing(true);
            const batchesToPublish = selectedRows
                .filter(batch => {
                    const batchData = batchExecutionList.find(
                        b => b.batchId === batch.batchId && b.date === batch.date
                    );
                    return batchData &&
                        batchData.pendingCount === 0 &&
                        batchData.draftCount > 0;
                })
                .map(batch => ({
                    batchId: batch.batchId,
                    date: batch.date
                }));
            if (batchesToPublish.length === 0) {
                showErrorAlert('No valid batches selected for publishing. Make sure they are in "Ready to Publish" state.');
                return;
            }
            const response = await bulkPublishBatchExecution(batchesToPublish);
            showSuccessAlert(`${batchesToPublish.length} batch(es) published successfully!`);
            setSelectedRows([]);
            fetchData();
        } catch (error) {
            showErrorAlert('An error occurred during bulk publish');
            console.error('Bulk publish error:', error);
        } finally {
            setBulkPublishing(false);
        }
    }, [selectedRows, batchExecutionList, fetchData, showSuccessAlert, showErrorAlert]);

    const isRowSelected = (batch) => {
        return selectedRows.some(
            item => item.batchId === batch.batchId && item.date === batch.date
        );
    };

    const isAllSelected = () => {
        return selectedRows.length > 0 &&
            selectedRows.length === batchExecutionList.length;
    };

    return (
        <React.Fragment>
            <SweetAlert
                success
                title="Success!"
                show={successAlert}
                onConfirm={() => setSuccessAlert(false)}
            >
                {alertMessage}
            </SweetAlert>
            <SweetAlert
                error
                title="Error!"
                show={errorAlert}
                onConfirm={() => setErrorAlert(false)}
            >
                {alertMessage}
            </SweetAlert>
            <BulkDeleteExecutionModal
                modalOpen={bulkDeleteModalOpen}
                closeModal={() => setBulkDeleteModalOpen(false)}
                batch={batchToDelete}
                onDeleteConfirmed={handleDeleteConfirmed}
                onSuccess={showSuccessAlert}
                onError={showErrorAlert}
            />
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Batch Executions" breadcrumbItems={breadcrumbItems} />
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <CardBody>
                                    <Row className="mt-2">
                                        <Col md={2}>
                                            <FormGroup>
                                                <Label>Show Entries</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) => {
                                                        const newLimit = parseInt(e.target.value);
                                                        setPageLimit(newLimit);
                                                        setCurrentPage(1);
                                                    }}
                                                    value={pageLimit}
                                                    disabled={loading}
                                                >
                                                    <option value={10}>10</option>
                                                    <option value={25}>25</option>
                                                    <option value={50}>50</option>
                                                    <option value={100}>100</option>
                                                </select>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup>
                                                <Label>Sort By</Label>
                                                <select
                                                    className="form-control"
                                                    onChange={(e) => {
                                                        setSortingRule(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                    value={sortingRule}
                                                    disabled={loading}
                                                >
                                                    <option value="date$desc">Date (Newest First)</option>
                                                    <option value="date$asc">Date (Oldest First)</option>
                                                </select>
                                            </FormGroup>
                                        </Col>
                                        <Col className="d-flex justify-content-end align-items-center">
                                            {selectedRows.length > 0 && (validatePermissionKey(authUser, PUBLISH_EXECUTION)) && (
                                                <Button
                                                    size='sm'
                                                    color="success"
                                                    className="mr-2"
                                                    onClick={handleBulkPublish}
                                                    disabled={bulkPublishing}
                                                >
                                                    {bulkPublishing ? (
                                                        <>
                                                            <Spinner size="sm" className="mr-2" />
                                                            Publishing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-upload mr-2"></i>
                                                            Publish Selected ({selectedRows.length})
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                            <Button
                                                size='sm'
                                                color="primary"
                                                className="mr-2"
                                                onClick={openFilterModal}
                                            >
                                                <i className="ri-filter-fill align-middle mr-2"></i>
                                                Filter Queries
                                                {currentFilterCount > 0 && (
                                                    <Badge color="light" pill className="ml-2">
                                                        {currentFilterCount}
                                                    </Badge>
                                                )}
                                            </Button>
                                            {validatePermissionKey(authUser, ADD_EXECUTION) && (
                                                <Button size='sm' color="success" onClick={openAddModal}>
                                                    <i className="ri-add-line align-middle mr-2"></i>
                                                    Add Execution
                                                </Button>
                                            )}
                                        </Col>
                                    </Row>
                                    {loading ? (
                                        <div className="text-center">
                                            <PageDataLoader />
                                        </div>
                                    ) : (
                                        <div className="table-rep-plugin">
                                            <div className="table-responsive mb-0">
                                                <Table striped bordered responsive>
                                                    <thead>
                                                        <tr>
                                                            {validatePermissionKey(authUser, PUBLISH_EXECUTION) && (
                                                                <th>
                                                                    <FormGroup check className="mb-4">
                                                                        <Input
                                                                            type="checkbox"
                                                                            checked={isAllSelected()}
                                                                            onChange={selectAllRows}
                                                                        />
                                                                    </FormGroup>
                                                                </th>
                                                            )}
                                                            <th>SL. No</th>
                                                            <th>Date</th>
                                                            <th>Batch Name</th>
                                                            <th >
                                                                Children
                                                            </th>
                                                            <th>Status</th>
                                                            <th>Actions</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {batchExecutionList.length > 0 ? (
                                                            batchExecutionList.map((batch, index) => {
                                                                const isSelected = isRowSelected(batch);
                                                                return (
                                                                    <tr
                                                                        key={`${batch.batchId}-${batch.date}`}
                                                                        className={isSelected ? "table-primary" : ""}
                                                                    >
                                                                        {(validatePermissionKey(authUser, PUBLISH_EXECUTION)) && (<td>
                                                                            <FormGroup check>
                                                                                <Input
                                                                                    type="checkbox"
                                                                                    checked={isSelected}
                                                                                    onChange={() => toggleRowSelection(batch)}
                                                                                />
                                                                            </FormGroup>
                                                                        </td>)}
                                                                        <td>{(currentPage - 1) * pageLimit + index + 1}</td>
                                                                        <td>{moment(batch.date).format('DD/MM/YY')}</td>
                                                                        <td>
                                                                            <Link to={`/batches/view/${batch.batchId}`}>{batch.batchName}</Link>
                                                                        </td>
                                                                        <td>{`${batch.totalStudents - batch.pendingCount}/${batch.totalStudents}`}</td>
                                                                        <td>
                                                                            {getStatusBadge(
                                                                                batch.pendingCount,
                                                                                batch.totalStudents,
                                                                                batch.draftCount
                                                                            )}
                                                                        </td>
                                                                        <td>
                                                                            {validatePermissionKey(authUser, VIEW_EXECUTIONS) && (
                                                                                <Button
                                                                                    color="primary"
                                                                                    size="sm"
                                                                                    className="mr-2"
                                                                                    onClick={() => history.push({
                                                                                        pathname: `/executions/create/${batch.batchId}`,
                                                                                        state: {
                                                                                            date: moment(batch.date).format(
                                                                                                'YYYY-MM-DD'
                                                                                            )
                                                                                        }
                                                                                    })}
                                                                                >
                                                                                    <i className="fas fa-eye mr-2"></i>
                                                                                    View
                                                                                </Button>
                                                                            )}
                                                                            {validatePermissionKey(authUser, PUBLISH_EXECUTION) && (
                                                                                <Button
                                                                                    color={(batch.pendingCount === 0 && batch.draftCount > 0) ? 'success' : 'secondary'}
                                                                                    size="sm"
                                                                                    className="mr-2"
                                                                                    onClick={() => handlePublishBatch(batch.batchId, batch.date)}
                                                                                    disabled={
                                                                                        (batch.pendingCount === 0 && batch.draftCount === 0) || (batch.pendingCount > 0) ||
                                                                                        publishingBatchId === batch.batchId
                                                                                    }
                                                                                >
                                                                                    {publishingBatchId === batch.batchId ? (
                                                                                        <>
                                                                                            <Spinner size="sm" className="mr-2" />
                                                                                            Publishing...
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <i className="fas fa-upload mr-2"></i>
                                                                                            Publish
                                                                                        </>
                                                                                    )}
                                                                                </Button>
                                                                            )}
                                                                            {validatePermissionKey(authUser, DELETE_EXECUTION) && (
                                                                                <Button
                                                                                    color="danger"
                                                                                    size="sm"
                                                                                    className=" waves-effect waves-light"
                                                                                    onClick={() => handleDeleteBatch(batch)}
                                                                                    disabled={deletingBatchId === batch._id}
                                                                                >
                                                                                    {deletingBatchId === batch._id ? (
                                                                                        <>
                                                                                            <Spinner size="sm" className="mr-2" />
                                                                                            Deleting...
                                                                                        </>
                                                                                    ) : (
                                                                                        <>
                                                                                            <i className="fas fa-trash mr-2"></i>
                                                                                            Delete
                                                                                        </>
                                                                                    )}
                                                                                </Button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="7" className="text-center">
                                                                    No batch executions found
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </div>
                                    )}
                                    <Row className="mt-2">
                                        <Col md="5">
                                            <p>
                                                <strong>
                                                    Showing {batchExecutionList.length > 0 ? (currentPage - 1) * pageLimit + 1 : 0} to{' '}
                                                    {Math.min(
                                                        currentPage * pageLimit,
                                                        totalEntries
                                                    )}{' '}
                                                    of {totalEntries} entries
                                                </strong>
                                            </p>
                                        </Col>
                                        {!loading && batchExecutionList.length > 0 && totalPages > 1 && (
                                            <Col md="7" className="d-flex justify-content-end">
                                                <Paginator
                                                    totalPages={totalPages}
                                                    currentPage={currentPage}
                                                    changePage={handlePagination}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
                {/* Filter Modal */}
                <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
                    <ModalHeader toggle={() => setFilterModalOpen(false)}>
                        Filter Batch Executions
                    </ModalHeader>
                    <ModalBody>
                        {filterError && <Alert color="danger">{filterError}</Alert>}
                        <FormGroup>
                            <Label>Status</Label>
                            <Input
                                type="select"
                                value={tempStatus}
                                onChange={(e) => setTempStatus(e.target.value)}
                            >
                                <option value="">All Statuses</option>
                                <option value="draft">Draft</option>
                                <option value="ready">Ready to Publish</option>
                                <option value="published">Published</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Batch</Label>
                            <Select
                                isMulti
                                options={filterBatchOptions}
                                value={filterBatchOptions.filter(option => tempBatchIds.includes(option.value))}
                                onChange={(selected) => {
                                    setTempBatchIds(selected ? selected.map(option => option.value) : []);
                                }}
                                placeholder="Select batches..."
                                isLoading={loadingFilterBatches}
                                isSearchable
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                    })
                                }}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Date</Label>
                            <Row>
                                <Col md={12}>
                                    <DatePicker
                                        selected={tempStartDate}
                                        onChange={date => setTempStartDate(date)}
                                        className="form-control"
                                        placeholderText="Select Date"
                                        dateFormat="yyyy-MM-dd"
                                        isClearable
                                    />
                                </Col>
                            </Row>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" size='sm' onClick={applyFilters}>
                            Apply Filters
                        </Button>
                        <Button color="success" size='sm' onClick={clearAllFilters}>
                            Reset
                        </Button>
                        <Button size="sm" color="light" onClick={() => setFilterModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
                {/* Add Execution Modal */}
                <Modal isOpen={addModalOpen} toggle={() => setAddModalOpen(false)} size="xl" style={{ maxHeight: '90%', overflowY: 'auto' }}>
                    <ModalHeader toggle={() => setAddModalOpen(false)}>
                        Add New Batch Execution
                    </ModalHeader>
                    <ModalBody>
                        {formError && <Alert color="danger">{formError}</Alert>}
                        <Form>
                            <div style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '15px' }}>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Batch *</Label>
                                            <Select
                                                options={batchOptions}
                                                value={commonBatch}
                                                onChange={setCommonBatch}
                                                placeholder="Select batch..."
                                                isLoading={loadingBatches}
                                                isSearchable
                                                styles={{
                                                    control: (provided) => ({
                                                        ...provided,
                                                        minHeight: '38px'
                                                    })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label>Execution Date *</Label>
                                            <DatePicker
                                                selected={commonDate}
                                                onChange={setCommonDate}
                                                className="form-control"
                                                dateFormat="yyyy-MM-dd"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </div>
                            {executionArray.map((element, index) => {
                                const availableChildren = getAvailableChildren(index);
                                
                                return (
                                    <div key={index} style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '5px', marginBottom: '15px' }}>
                                        <Row>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Child *</Label>
                                                    <Select
                                                        options={availableChildren}
                                                        value={element.child}
                                                        onChange={(e) => handleChildSelection(index, e)}
                                                        placeholder={commonBatch ? "Select child..." : "Select batch first"}
                                                        isLoading={loadingChildren}
                                                        isSearchable
                                                        isDisabled={!commonBatch}
                                                        styles={{
                                                            control: (provided) => ({
                                                                ...provided,
                                                                minHeight: '38px',
                                                                backgroundColor: !commonBatch ? '#e9ecef' : 'white'
                                                            })
                                                        }}
                                                    />
                                                    {loadingChildren && (
                                                        <div className="mt-1 text-muted small">
                                                            Loading children for selected batch...
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                            <Col md={6}>
                                                <FormGroup>
                                                    <Label>Attendance</Label>
                                                    <Select
                                                        options={[
                                                            { value: false, label: 'Absent' },
                                                            { value: true, label: 'Present' }
                                                        ]}
                                                        onChange={(e) => {
                                                            handleIsPresent(index, e.value);
                                                        }}
                                                        defaultValue={{ value: true, label: 'Present' }}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <FormGroup>
                                            <Label>Note{noteRequired === false ? ' (optional)' : ''}</Label>
                                            <Input
                                                type="textarea"
                                                value={element.note}
                                                onChange={(e) => handleNote(index, e)}
                                                rows="3"
                                            />
                                        </FormGroup>
                                        <div className="mt-2" style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Button
                                                    color="info"
                                                    size="sm"
                                                    onClick={() => toggleMultimedia(index)}
                                                    className="mb-2"
                                                >
                                                    {element.multimediaOpen ? (
                                                        <>
                                                            <FaMinus className="mr-1" /> Hide Multimedia
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaPlus className="mr-1" /> Add Multimedia
                                                        </>
                                                    )}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="danger"
                                                    className="mb-2"
                                                    disabled={index === 0}
                                                    onClick={() => removeExecutionFields(index)}
                                                >
                                                    <i className="ri-delete-bin-6-line align-middle"></i> Remove
                                                </Button>
                                        </div>
                                        <Collapse isOpen={element.multimediaOpen}>
                                            <div className="border rounded p-3 bg-light">
                                                <h6 className="mb-3">Multimedia Content</h6>
                                                <FormGroup>
                                                    <Label>Upload Images/Videos</Label>
                                                    <FormUpload
                                                        setImages={(files) => {
                                                            const newExecutionArray = [...executionArray];
                                                            newExecutionArray[index].images = files;
                                                            setExecutionArray(newExecutionArray);
                                                        }}
                                                        multiple={true}
                                                        title="Upload Images or Videos"
                                                        video={true}
                                                    />
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>YouTube Links</Label>
                                                    <InputGroup>
                                                        <InputGroupAddon addonType="prepend">
                                                            <InputGroupText>
                                                                <FaYoutube className="text-danger" />
                                                            </InputGroupText>
                                                        </InputGroupAddon>
                                                        <Input
                                                            type="text"
                                                            placeholder="Enter YouTube link"
                                                            value={element.tempLink || ''}
                                                            onChange={(e) => {
                                                                const newExecutionArray = [...executionArray];
                                                                newExecutionArray[index].tempLink = e.target.value;
                                                                setExecutionArray(newExecutionArray);
                                                            }}
                                                        />
                                                        <InputGroupAddon addonType="append">
                                                            <Button
                                                                color="primary"
                                                                onClick={() => handleAddLink(index, element.tempLink || '')}
                                                                disabled={!element.tempLink}
                                                            >
                                                                Add
                                                            </Button>
                                                        </InputGroupAddon>
                                                    </InputGroup>
                                                    {element.links && element.links.length > 0 && (
                                                        <div className="mt-3">
                                                            <ul className="list-group">
                                                                {element.links.map((link, linkIndex) => (
                                                                    <li
                                                                        key={linkIndex}
                                                                        className="list-group-item d-flex justify-content-between align-items-center py-2"
                                                                    >
                                                                        <a
                                                                            href={link}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="text-truncate"
                                                                            style={{ maxWidth: '80%' }}
                                                                        >
                                                                            {link}
                                                                        </a>
                                                                        <Button
                                                                            color="danger"
                                                                            size="sm"
                                                                            onClick={() => handleRemoveLink(index, link)}
                                                                        >
                                                                            <i className="fas fa-times"></i>
                                                                        </Button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })}
                            <Row className="mt-2 mb-2">
                                <Col className="d-flex justify-content-start align-items-center">
                                    <Button
                                        size="sm"
                                        color="info"
                                        className=" waves-effect waves-light"
                                        onClick={() => addExecutionFields()}
                                        disabled={
                                            !executionArray[executionArray.length - 1].child ||
                                            (noteRequired !== false &&
                                                executionArray[executionArray.length - 1].note === '') ||
                                            executionArray.length >= allChildOptions.length
                                        }
                                    >
                                        <i className="ri-add-line align-middle mr-2"></i>
                                        Add More Children
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setAddModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={() => handleAddExecution('draft')}
                            disabled={savingAsDraft}
                        >
                            {savingAsDraft ? (
                                <>
                                    <Spinner size="sm" className="mr-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save as Draft'
                            )}
                        </Button>
                        <Button
                            color="success"
                            onClick={() => handleAddExecution('publish')}
                            disabled={publishingNow}
                        >
                            {publishingNow ? (
                                <>
                                    <Spinner size="sm" className="mr-2" />
                                    Publishing...
                                </>
                            ) : (
                                'Publish Now'
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = (state) => ({
    authUser: state.Login.user
});

export default connect(mapStateToProps)(BatchExecutionListPage);